import { useEffect, useRef } from 'react';
import type { AdBannerSize } from 'shared/components/ad-banner';
import type { AdAnimal } from 'shared/components/ad-banner/ad-animal-context';
import { ensureGoogletagCommandQueue } from 'shared/components/ad-banner/google-ad-manager';
import { env } from 'shared/library/env';
import { monitoring } from 'shared/library/monitoring';
import { getCookieConsent } from 'shared/library/cookie-consent';

let servicesEnabled = false;
let googleAdManagerLoad: Promise<void> | null = null;
const GOOGLE_AD_MANAGER_SCRIPT_ID = 'google-admanager';
const GOOGLE_AD_MANAGER_SRC = 'https://pagead2.googlesyndication.com/tag/js/gpt.js';

function logAdEvent(event: string, data: Record<string, unknown>) {
  if (env.IS_DEV || env.E2E) {
    console.info(`[AdBanner] ${event} ${JSON.stringify(data)}`);
  }
}

function enableServicesOnce(gtag: any) {
  if (!servicesEnabled) {
    gtag.setConfig({
      disableInitialLoad: true,
      singleRequest: true,
    });
    gtag.enableServices();
    servicesEnabled = true;
  }
}

type BannerProps = AdBannerSize & {
  animal: AdAnimal;
  isRendered: boolean;
  loadImmediately: boolean;
  onEmpty?: () => void;
  onRendered?: () => void;
};

export default function Banner({
  animal,
  width,
  height,
  isRendered,
  loadImmediately,
  onEmpty,
  onRendered,
}: BannerProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<any>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const slotPath = '/21877973041/panorama';
  const renderCount = useRef(0);

  renderCount.current += 1;

  useEffect(() => {
    monitoring.Sentry.addBreadcrumb({
      category: 'ad_banner',
      message: 'AdBanner render',
      level: 'info',
      data: { width, height, renderCount: renderCount.current },
    });
  }, [width, height]);

  useEffect(() => {
    if (!divRef.current) return;

    let isActive = true;
    const id = `banner-${Math.random().toString(36).slice(2, 9)}`;
    divRef.current.id = id;

    logAdEvent('mounted', { id, width, height, animal });

    monitoring.Sentry.addBreadcrumb({
      category: 'ad_banner',
      message: 'AdBanner effect - waiting for googletag',
      level: 'info',
      data: { id },
    });

    // Prepare ads one screen ahead without requesting several screens of
    // creatives while the initial content is still loading.
    const rootMargin = `${window.innerHeight}px 0px`;
    let observer: IntersectionObserver | null = null;
    const loadAd = () => {
      if (!isActive) return;

      logAdEvent(loadImmediately ? 'loading immediately' : 'within load margin', {
        id,
        rootMargin,
      });

      void waitForGoogletag().then((gtag) => {
        gtag.cmd.push(() => {
          if (!isActive || !divRef.current) return;

          const handleSlotRenderEnded = (event: any) => {
            if (event.slot !== slotRef.current) return;

            logAdEvent('slotRenderEnded', {
              id,
              isEmpty: event.isEmpty,
              size: event.size,
              lineItemId: event.lineItemId,
              creativeId: event.creativeId,
            });

            if (event.isEmpty) {
              onEmpty?.();
            } else {
              onRendered?.();
            }
          };

          const forCurrentSlot = (listener: (event: any) => void) => (event: any) => {
            if (event.slot === slotRef.current) listener(event);
          };
          const slotEventListeners = [
            ['slotRequested', forCurrentSlot(() => logAdEvent('slotRequested', { id }))],
            [
              'slotResponseReceived',
              forCurrentSlot(() => logAdEvent('slotResponseReceived', { id })),
            ],
            ['slotOnload', forCurrentSlot(() => logAdEvent('slotOnload', { id }))],
            ['impressionViewable', forCurrentSlot(() => logAdEvent('impressionViewable', { id }))],
            [
              'slotVisibilityChanged',
              forCurrentSlot((event: any) =>
                logAdEvent('slotVisibilityChanged', {
                  id,
                  inViewPercentage: event.inViewPercentage,
                }),
              ),
            ],
          ] as const;

          monitoring.Sentry.addBreadcrumb({
            category: 'ad_banner',
            message: 'AdBanner googletag ready, defining slot',
            level: 'info',
            data: { id, slotPath },
          });

          // Personalized ads only after marketing consent; later slots pick up a new choice.
          const personalized = getCookieConsent()?.marketing === true;
          gtag.pubads().setPrivacySettings({
            nonPersonalizedAds: !personalized,
            limitedAds: !personalized,
          });

          enableServicesOnce(gtag);

          slotRef.current = gtag
            .defineSlot(slotPath, [width, height], id)
            ?.addService(gtag.pubads());

          if (!slotRef.current) return;

          slotRef.current.setConfig({ targeting: { animal } });

          gtag.pubads().addEventListener('slotRenderEnded', handleSlotRenderEnded);
          slotEventListeners.forEach(([eventName, listener]) => {
            gtag.pubads().addEventListener(eventName, listener);
          });
          gtag.pubads().refresh([slotRef.current]);

          cleanupRef.current = () => {
            gtag.pubads().removeEventListener('slotRenderEnded', handleSlotRenderEnded);
            slotEventListeners.forEach(([eventName, listener]) => {
              gtag.pubads().removeEventListener(eventName, listener);
            });
          };
        });
      });
    };

    if (loadImmediately || typeof window.IntersectionObserver !== 'function') {
      loadAd();
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          observer?.disconnect();
          loadAd();
        },
        { rootMargin },
      );
      observer.observe(divRef.current);
    }

    return () => {
      isActive = false;
      observer?.disconnect();
      logAdEvent('unmounted', { id });
      cleanupRef.current?.();
      cleanupRef.current = null;

      const slot = slotRef.current;
      slotRef.current = null;

      if (window.googletag && slot) {
        monitoring.Sentry.addBreadcrumb({
          category: 'ad_banner',
          message: 'AdBanner cleanup - destroying slot',
          level: 'info',
          data: { id },
        });

        window.setTimeout(() => {
          const googletag = ensureGoogletagCommandQueue(window.googletag);
          window.googletag = googletag;
          googletag.cmd.push(() => {
            googletag.destroySlots?.([slot]);
          });
        }, 0);
      }
    };
  }, [animal, height, loadImmediately, onEmpty, onRendered, width]);

  return (
    <div ref={divRef} style={{ width, height, visibility: isRendered ? 'visible' : 'hidden' }} />
  );
}

function waitForGoogletag(): Promise<any> {
  return new Promise((resolve) => {
    loadGoogleAdManager().then(() => {
      const googletag = ensureGoogletagCommandQueue(window.googletag);
      window.googletag = googletag;
      googletag.cmd.push(() => resolve(googletag));
    });
  });
}

function loadGoogleAdManager(): Promise<void> {
  window.googletag = ensureGoogletagCommandQueue(window.googletag);

  if (window.googletag?.apiReady) {
    return Promise.resolve();
  }

  if (googleAdManagerLoad) {
    return googleAdManagerLoad;
  }

  googleAdManagerLoad = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      GOOGLE_AD_MANAGER_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Google Ad Manager failed')),
        {
          once: true,
        },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_AD_MANAGER_SCRIPT_ID;
    script.async = true;
    script.src = GOOGLE_AD_MANAGER_SRC;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Google Ad Manager failed')), {
      once: true,
    });

    document.head.appendChild(script);
  }).catch((error) => {
    monitoring.Sentry.addBreadcrumb({
      category: 'ad_banner',
      message: 'Google Ad Manager failed to load',
      level: 'warning',
      data: { message: error instanceof Error ? error.message : String(error) },
    });
  });

  return googleAdManagerLoad;
}
